import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Select } from '@/lib/base/forms/select/select';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import { SelectInputError, SelectInputLoading } from './SelectInputStates';
import type { PropertyInputProps } from './TextInput';

/** Entity types whose properties are selectable in the property_type field. */
const SELECTABLE_ENTITY_TYPES = [
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_note',
] as const;

export const PropertyTypeSelectInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  size = 'sm',
}) => {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['property-types-for-select', orgId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      const client = createSchemaClient(orgId);
      return client.getPropertyTypes();
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(
    () =>
      (data ?? [])
        .filter(
          pt =>
            (SELECTABLE_ENTITY_TYPES as readonly string[]).includes(
              pt.entity_type_name
            ) && pt.value_write_access === 'all'
        )
        .map(pt => ({
          id: `${pt.entity_type_name}.${pt.property_type_name}`,
          label: pt.label,
          icon: getEntityTypeIconComponent(pt.entity_type_name),
        })),
    [data]
  );

  const Icon = getPropertyTypeIconComponent(propertyType);

  if (isLoading) {
    return <SelectInputLoading />;
  }

  if (error) {
    return <SelectInputError error={error} />;
  }

  return (
    <Select.ComboBox
      placeholder={placeholder || `Select ${propertyType.label.toLowerCase()}`}
      size={size}
      items={items}
      selectedKey={value !== null && value !== undefined ? String(value) : null}
      onSelectionChange={key =>
        onChange(key !== null && key !== undefined ? String(key) : null)
      }
      isDisabled={disabled}
      shortcut={false}
      aria-label={propertyType.label}
      icon={Icon}
      ref={inputRef}
    >
      {item => (
        <Select.Item id={item.id} icon={item.icon}>
          {item.label}
        </Select.Item>
      )}
    </Select.ComboBox>
  );
};
